/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { Ellipse } from './Ellipse';
import { Path } from './Path';
import { Polygon } from './Polygon';
import { Rect } from './Rect';
import { MarkerShapes } from './Shapes.types';

type MarkerProps = {
  scale: number;
  type: MarkerShapes;
  height?: number;
  width?: number;
  fill: ColorProps['color'];
  stroke: ColorProps['color'];
  preserveRectAspectRatio?: boolean;
};

const GOLDEN_RATIO = 1.62;
const HUMAN_SCALE_X = 0.8; // preserve legacy human marker scale w/h = 0.8

export const Marker = ({
  scale,
  type,
  height = 100,
  width = 100,
  fill,
  stroke,
  preserveRectAspectRatio
}: MarkerProps) => {
  const scaleX = type === 'human' ? scale * HUMAN_SCALE_X : scale;
  const scaleY = type === 'rectangle' && preserveRectAspectRatio ? scale / GOLDEN_RATIO : scale;
  const tx = ((1 - scaleX) * width) / 2;
  const ty = ((1 - scaleY) * height) / 2;
  const ry = (height / 2) * (type === 'ellipse' && preserveRectAspectRatio ? 1 / GOLDEN_RATIO : 1);

  switch (type) {
    case 'human': {
      return (
        <Path
          type="human"
          tx={tx}
          ty={ty}
          fill={fill}
          stroke={stroke}
          width={width}
          height={height}
          scaleX={scaleX}
          scaleY={scaleY}
        />
      );
    }
    case 'rectangle':
    case 'square': {
      return (
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          scaleX={scaleX}
          scaleY={scaleY}
          fill={fill}
          stroke={stroke}
          tx={tx}
          ty={ty}
        />
      );
    }
    case 'circle':
    case 'ellipse': {
      return (
        <Ellipse
          cx={width / 2}
          cy={height / 2}
          scaleX={scaleX}
          scaleY={scaleY}
          fill={fill}
          stroke={stroke}
          rx={width / 2}
          ry={ry}
          tx={0}
          ty={0}
        />
      );
    }
    default: {
      return (
        <Polygon
          fill={fill}
          stroke={stroke}
          type={type}
          scaleX={scaleX}
          scaleY={scale}
          tx={tx}
          ty={ty}
          height={height}
          width={width}
        />
      );
    }
  }
};
